import { useCallback, useMemo } from 'react';

import { type Vendor } from '@sg/graphql-schema';
import { type useCustomizationExternalActions } from '@sg/shared-fe-features-customization';

import { telemetryProductFromInput, useTelemetry } from '@order/Telemetry';

import {
  useAddLineItemToCart,
  useCart,
  useEditLineItemInCart,
  useRemoveLineItemFromCart,
} from '../../../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const useCustomizeCartMutations = (params: Params) => {
  const {
    addressId,
    deliveryFee,
    vendor,
    onAddLineItemToCart,
    onEditLineItemInCart,
    onRemoveLineItemFromCart,
    sharedLineItemSlug,
  } = params;

  const { cart, cartOrderType, total, orderId } = useCart();
  const { track } = useTelemetry();

  const addLineItemToCartMutation = useAddLineItemToCart({ onSuccess: onAddLineItemToCart }); // prettier-ignore
  const editLineItemInCartMutation = useEditLineItemInCart({ onSuccess: onEditLineItemInCart }); // prettier-ignore
  const removeFromCartMutation = useRemoveLineItemFromCart({ onSuccess: onRemoveLineItemFromCart }); // prettier-ignore

  const { addLineItemToCart, fetching: isAddingLineItemToCart } = addLineItemToCartMutation; // prettier-ignore
  const { editLineItemInCart, fetching: isEditingLineItemInCart } = editLineItemInCartMutation; // prettier-ignore
  const { removeLineItemFromCart, fetching: isRemovingLineItemFromCart } = removeFromCartMutation; // prettier-ignore

  // ─── Derived Data ────────────────────────────────────────────────────────────

  const deliveryDetails = useMemo(() => {
    if (!addressId) return;

    return {
      tip: 0,
      addressId,
      vendor: vendor?.name ?? '',
      vendorRestaurantId: vendor?.restaurantId ?? '',
      deliveryFee: deliveryFee ?? 0,
    };
  }, [vendor, addressId, deliveryFee]);

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  const onAddLineItemToCartTelemetry = useCallback(
    (params: OnCustomizationCompleteParams) => {
      const { product, quantity, restaurant, modifications } = params;

      track('pdp.add_to_bag', {
        orderId,
        restaurantId: restaurant?.id,
        restaurantName: restaurant?.name,
        orderValue: total,
        orderType: cartOrderType,
        bagCount: cart?.lineItems?.length,
        ...(sharedLineItemSlug ? { isSharedLineItem: true, sharedLineItemSlug } : {}),
        selectedProduct: telemetryProductFromInput(
          { ...product, quantity, location: restaurant?.slug },
          modifications,
        ),
      });
    },
    [cart?.lineItems?.length, cartOrderType, orderId, sharedLineItemSlug, total, track],
  );

  const modifyCart = useCallback(
    async (params: OnCustomizationCompleteParams) => {
      const { product, lineItemId, quantity, modifications, mixedDressingDetails, totalCost } =
        params;

      if (!product) return;

      onAddLineItemToCartTelemetry(params);

      const modificationsInput = {
        additions: modifications?.additions ?? [],
        removals: modifications?.removals ?? [],
        substitutions: modifications?.substitutions ?? [],
        mixedDressingDetails: mixedDressingDetails ?? [],
      };

      // ─── Edit Line Item ──────────────────────────────────────────

      if (lineItemId) {
        await editLineItemInCart({
          product,
          lineItemId,
          quantity,
          modifications: modificationsInput,
        });

        return;
      }

      // ─── Add Line Item ────────────────────────────────────────────

      await addLineItemToCart({
        product,
        quantity,
        deliveryDetails,
        sharedLineItemSlug,
        totalCost,
        modifications: modificationsInput,
      });
    },
    [
      onAddLineItemToCartTelemetry,
      addLineItemToCart,
      deliveryDetails,
      editLineItemInCart,
      sharedLineItemSlug,
    ],
  );

  // ─────────────────────────────────────────────────────────────────────

  return {
    removeLineItemFromCart,
    modifyCart,
    isAddingLineItemToCart,
    isEditingLineItemInCart,
    isRemovingLineItemFromCart,
  };
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  addressId?: string;
  deliveryFee?: number;
  vendor?: Vendor;
  onAddLineItemToCart?: () => void;
  onEditLineItemInCart?: () => void;
  onRemoveLineItemFromCart?: () => void;
  sharedLineItemSlug?: string;
};

type OnCustomizationCompleteParams = NonNullable<
  Parameters<Callbacks['onCustomizationComplete']>[0]
>;

type Callbacks = Required<Parameters<typeof useCustomizationExternalActions>[0]>;
